import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { BarChart3 } from 'lucide-react';

const ASRPerformanceHeatmap = ({ onFilterChange, filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [heatmapData, setHeatmapData] = useState({ rows: [], cols: [], cells: [] });
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null);
  const [granularity, setGranularity] = useState('hour');

  useEffect(() => {
    fetchHeatmapData();
  }, [filters, granularity]);

  const fetchHeatmapData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/asr-performance?granularity=${granularity}`
      );
      const result = await response.json();
      setHeatmapData(result);
    } catch (error) {
      console.error('Error fetching ASR performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCellData = (row, col) => {
    return heatmapData.cells.find(c => c.row === row && c.col === col) || null;
  };

  const getWERColor = (wer) => {
    // WER: 0 = perfect (green), 0.5+ = poor (red)
    if (wer === null) return 'rgba(100, 100, 100, 0.2)';
    
    // Green to Red gradient based on WER
    if (wer < 0.05) return 'rgba(16, 185, 129, 0.9)';  // Emerald (excellent)
    if (wer < 0.10) return 'rgba(34, 197, 94, 0.8)';   // Green (good)
    if (wer < 0.15) return 'rgba(234, 179, 8, 0.7)';   // Yellow (fair)
    if (wer < 0.25) return 'rgba(249, 115, 22, 0.8)';  // Orange (poor)
    return 'rgba(239, 68, 68, 0.9)';                   // Red (bad)
  };

  const handleCellClick = (row, col, cellData) => {
    if (!cellData) return;
    
    setSelectedCell({ row, col });
    
    if (onFilterChange) {
      const [vendor, model] = row.split(':');
      onFilterChange({
        asr_vendor: vendor,
        asr_model: model,
        hour_or_campaign: col,
        type: 'asr-heatmap'
      });
    }
  };

  if (loading) {
    return (
      <Card className="glass-card" data-testid="asr-performance-heatmap">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
            ASR Performance Heatmap
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-96">
            <div className="animate-pulse text-slate-400">Loading ASR performance data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const animationDuration = reducedMotion ? 0 : 0.5;

  return (
    <Card className="glass-card" data-testid="asr-performance-heatmap">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              ASR Performance Heatmap
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Color = median WER (green=good, red=poor) • Label = model latency • Click to filter
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setGranularity('hour')}
              className={`px-3 py-1 text-xs rounded transition-all ${
                granularity === 'hour'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              By Hour
            </button>
            <button
              onClick={() => setGranularity('campaign')}
              className={`px-3 py-1 text-xs rounded transition-all ${
                granularity === 'campaign'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              By Campaign
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {heatmapData.rows.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No ASR performance data available yet</p>
            <p className="text-xs mt-2">Data will appear once calls have WER scores</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left text-xs text-slate-400 p-2">ASR Vendor/Model</th>
                  {heatmapData.cols.map(col => (
                    <th key={col} className="text-center text-xs text-slate-400 p-2">
                      {granularity === 'hour' ? `${col}:00` : col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmapData.rows.map((row, rowIndex) => (
                  <tr key={row}>
                    <td className="text-sm text-slate-300 p-2 font-medium">{row}</td>
                    {heatmapData.cols.map((col, colIndex) => {
                      const cellData = getCellData(row, col);
                      const isSelected = selectedCell?.row === row && selectedCell?.col === col;
                      
                      return (
                        <td key={`${row}-${col}`} className="p-1">
                          <motion.div
                            className="relative h-16 rounded-lg cursor-pointer overflow-hidden"
                            style={{
                              backgroundColor: cellData ? getWERColor(cellData.median_wer) : 'rgba(100, 100, 100, 0.1)',
                              border: isSelected ? '2px solid #a855f7' : '1px solid rgba(255,255,255,0.1)'
                            }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ 
                              opacity: 1, 
                              scale: 1,
                              backgroundColor: cellData ? getWERColor(cellData.median_wer) : 'rgba(100, 100, 100, 0.1)',
                              transition: { 
                                delay: reducedMotion ? 0 : (rowIndex * heatmapData.cols.length + colIndex) * 0.01,
                                duration: animationDuration,
                                ease: 'easeInOut'
                              }
                            }}
                            whileHover={reducedMotion ? {} : { scale: 1.05, zIndex: 10 }}
                            onClick={() => handleCellClick(row, col, cellData)}
                          >
                            {cellData && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                                <div className="text-lg font-bold">
                                  {cellData.median_wer !== null ? (cellData.median_wer * 100).toFixed(1) + '%' : '—'}
                                </div>
                                <div className="text-xs opacity-80">
                                  {cellData.median_model_latency_ms ? `${cellData.median_model_latency_ms}ms` : ''}
                                </div>
                                <div className="text-xs opacity-60 mt-1">
                                  n={cellData.count}
                                </div>
                              </div>
                            )}
                            
                            {/* Tooltip */}
                            {cellData && (
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded shadow-lg opacity-0 hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
                                <div className="font-semibold">{row}</div>
                                <div>WER: {cellData.median_wer !== null ? (cellData.median_wer * 100).toFixed(2) + '%' : 'N/A'}</div>
                                <div>Latency: {cellData.median_model_latency_ms || 'N/A'}ms</div>
                                <div>Samples: {cellData.count}</div>
                              </div>
                            )}
                          </motion.div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 flex items-center gap-6 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <span>WER (Word Error Rate):</span>
            <div className="flex gap-1">
              <div className="w-6 h-4 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 0.9)' }} title="<5%" />
              <div className="w-6 h-4 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.8)' }} title="5-10%" />
              <div className="w-6 h-4 rounded" style={{ backgroundColor: 'rgba(234, 179, 8, 0.7)' }} title="10-15%" />
              <div className="w-6 h-4 rounded" style={{ backgroundColor: 'rgba(249, 115, 22, 0.8)' }} title="15-25%" />
              <div className="w-6 h-4 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.9)' }} title=">25%" />
            </div>
            <span className="ml-2">Excellent → Poor</span>
          </div>
          
          {heatmapData.sample_size > 0 && (
            <div>
              Total samples: {heatmapData.sample_size}
              {heatmapData.null_fields > 0 && (
                <span className="ml-2 text-amber-400">
                  ({heatmapData.null_fields} cells with thin data)
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ASRPerformanceHeatmap;
